import { SxProps, Theme, alpha } from "@mui/material/styles";
import {
  INACTIVE_STORE_STATE,
  InactiveStoreBranchResponse,
  InactiveStoreCountsResponse,
  InactiveStoreExportFormat,
  InactiveStoreFilterParams,
  InactiveStoreState,
  InactiveStoreSummaryResponse,
} from "../../types/inactive-stores.type";
import { formatCount } from "../VisitFrequency/visitFrequency";

// ── Threshold ─────────────────────────────────────────────────────────────────────────────

/** Finance's usual cut-offs; 4 days is the "ทั้งหมด" sheet of their Excel file. */
export const THRESHOLD_PRESETS = [
  { days: 4, label: "ทั้งหมด (เกิน 4 วัน)" },
  { days: 7, label: "เกิน 7 วัน" },
  { days: 14, label: "เกิน 14 วัน" },
  { days: 30, label: "เกิน 30 วัน" },
] as const;

export const CUSTOM_THRESHOLD = "custom";
export const CUSTOM_THRESHOLD_LABEL = "กำหนดเอง";
export const MIN_DAYS = 1;

/**
 * The custom threshold's range hint. The top of the range is the server's bill lookback
 * (`lookback_days`, a system setting): a longer threshold could never match.
 */
export const customDaysHint = (maxDays: number): string => `ระบุ ${MIN_DAYS}–${maxDays} วัน`;

const WHOLE_NUMBER_PATTERN = /^\d+$/;

/** A typed custom threshold as whole days in range, or `null` when it is not one. */
export const parseCustomDays = (text: string, maxDays: number): number | null => {
  const trimmed = text.trim();
  if (!WHOLE_NUMBER_PATTERN.test(trimmed)) return null;
  const days = Number(trimmed);
  return days >= MIN_DAYS && days <= maxDays ? days : null;
};

// ── Detail filters ────────────────────────────────────────────────────────────────────────

export const PAGE_SIZE = 100;
export const SEARCH_DEBOUNCE_MS = 400;

/** UI value for "no filter" in the selects; never sent to the API. */
export const ALL_OPTION = "all";

export interface StoreFilterValues {
  branchCode: string;
  van: string;
  shopGroup: string;
  search: string;
}

export const DEFAULT_FILTERS: StoreFilterValues = {
  branchCode: ALL_OPTION,
  van: ALL_OPTION,
  shopGroup: ALL_OPTION,
  search: "",
};

const optionParam = (value: string): string | undefined =>
  value === ALL_OPTION ? undefined : value;

/** The request both the detail table and its export send, so the file matches the screen. */
export const toFilterParams = (
  days: number,
  filters: StoreFilterValues,
): InactiveStoreFilterParams => ({
  days,
  branch_code: optionParam(filters.branchCode),
  van: optionParam(filters.van),
  shop_group: optionParam(filters.shopGroup),
  search: filters.search.trim() || undefined,
});

/**
 * A new threshold can take a van or GROUP out of the options, and a filter left on it would
 * quietly empty the table. สาขา and the search survive: the branches never change with it.
 */
export const reconcileFilters = (
  filters: StoreFilterValues,
  summary: InactiveStoreSummaryResponse,
): StoreFilterValues => ({
  ...filters,
  van: summary.van_options.includes(filters.van) ? filters.van : ALL_OPTION,
  shopGroup: summary.group_options.includes(filters.shopGroup) ? filters.shopGroup : ALL_OPTION,
});

/** A pivot row click filters to that branch; clicking the same row again clears it. */
export const toggleBranchFilter = (current: string, branchCode: string): string =>
  current === branchCode ? ALL_OPTION : branchCode;

// ── Labels ────────────────────────────────────────────────────────────────────────────────

export const ALL_BRANCHES_LABEL = "ทุกสาขา";
export const EMPTY_TEXT = "ไม่พบร้านตามเงื่อนไข";
export const LOAD_ERROR_TEXT = "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
export const LIST_LOAD_ERROR_TEXT = "โหลดรายชื่อร้านไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
export const MISSING = "—";

/** "ระยอง 09-RY"; the code alone when the branch has no name. */
export const branchOptionLabel = (
  branch: Pick<InactiveStoreBranchResponse, "branch_code" | "branch_name">,
): string => (branch.branch_name ? `${branch.branch_name} ${branch.branch_code}` : branch.branch_code);

export const noBillLabel = (lookbackDays: number): string => `ไม่มีบิลใน ${lookbackDays} วัน`;

export const storeCountLabel = (count: number): string => `${formatCount(count)} ร้าน`;

export const displayedRowsLabel = ({
  from,
  to,
  count,
}: {
  from: number;
  to: number;
  count: number;
}): string => `${formatCount(from)}-${formatCount(to)} จาก ${formatCount(count)}`;

// ── Pivot columns and the popup behind a count ────────────────────────────────────────────

/** TOTAL and On keep the client's own labels; Pause is ปิดใช้งาน because tab 1 already uses
 * พักใช้งาน for a suspended shop. */
export const COLUMN_LABEL = {
  total: "TOTAL",
  active: "On",
  disabled: "ปิดใช้งาน",
} as const;

type ShopCountKind = keyof typeof COLUMN_LABEL;

export const GROUP_KIND = "group";

export type PivotColumn = { kind: ShopCountKind } | { kind: typeof GROUP_KIND; group: string };

const SHOP_COUNT_FIELD: Record<ShopCountKind, "total_shops" | "active_shops" | "disabled_shops"> = {
  total: "total_shops",
  active: "active_shops",
  disabled: "disabled_shops",
};

/** Which shops the popup behind each kind of count lists. */
const SHOP_COUNT_STATE: Record<ShopCountKind, InactiveStoreState> = {
  total: INACTIVE_STORE_STATE.all,
  active: INACTIVE_STORE_STATE.active,
  disabled: INACTIVE_STORE_STATE.disabled,
};

/** TOTAL | On | ปิดใช้งาน, then one column per GROUP in the server's order. */
export const pivotColumns = (groupColumns: string[]): PivotColumn[] => [
  { kind: "total" },
  { kind: "active" },
  { kind: "disabled" },
  ...groupColumns.map((group): PivotColumn => ({ kind: GROUP_KIND, group })),
];

export const columnKey = (column: PivotColumn): string =>
  column.kind === GROUP_KIND ? `${GROUP_KIND}-${column.group}` : column.kind;

/** The pivot header: a GROUP column is headed by its letter alone. */
export const columnHeader = (column: PivotColumn): string =>
  column.kind === GROUP_KIND ? column.group : COLUMN_LABEL[column.kind];

/** The popup title names the column in full. */
export const columnLabel = (column: PivotColumn): string =>
  column.kind === GROUP_KIND ? `GROUP ${column.group}` : COLUMN_LABEL[column.kind];

export const countFor = (counts: InactiveStoreCountsResponse, column: PivotColumn): number =>
  column.kind === GROUP_KIND
    ? counts.group_shops[column.group] ?? 0
    : counts[SHOP_COUNT_FIELD[column.kind]];

export interface PivotCellSelection {
  /** `null` for the รวมทั้งหมด row: every branch in scope. */
  branch: InactiveStoreBranchResponse | null;
  column: PivotColumn;
  count: number;
}

/** The list request behind a clicked count. */
export const cellQuery = (
  column: PivotColumn,
): { state: InactiveStoreState; shop_group?: string } =>
  column.kind === GROUP_KIND
    ? { state: INACTIVE_STORE_STATE.active, shop_group: column.group }
    : { state: SHOP_COUNT_STATE[column.kind] };

export const cellTitle = ({ branch, column }: PivotCellSelection): string =>
  `${branch ? branchOptionLabel(branch) : ALL_BRANCHES_LABEL} · ${columnLabel(column)}`;

/** Only TOTAL mixes On and ปิดใช้งาน shops, so only its popup needs a สถานะ column. */
export const showsStatusColumn = (column: PivotColumn): boolean => column.kind === "total";

export const statusLabel = (isDisabled: boolean): string =>
  isDisabled ? COLUMN_LABEL.disabled : COLUMN_LABEL.active;

// ── Cell colours (FIN Excel's conditional formatting, on our palette) ─────────────────────

/** A GROUP cell above this many shops is red; from 1 up to it, yellow; 0 is green. */
const GROUP_WARNING_MAX = 10;

/** จำนวนวัน colours: red from 30 days, orange from 14, yellow from 7. */
const DAYS_TONE_FLOORS = { critical: 30, warning: 14, notice: 7 } as const;

/**
 * One token per styled tier. Tiers may share a palette colour (GROUP 1–10 and Days ≥ 14 are both
 * warning) and still differ in strength, which a bare colour name could not express.
 */
export type CellTone =
  | "groupZero"
  | "groupLow"
  | "groupHigh"
  | "daysNotice"
  | "daysWarning"
  | "daysCritical"
  | "totalRow";

const TONE_ALPHA = 0.12;
const STRONG_TONE_ALPHA = 0.24;
const EMPHASIS_WEIGHT = 600;
const TOTAL_ROW_WEIGHT = 700;

interface ToneStyle {
  color: "success" | "warning" | "error";
  shade: "main" | "light";
  tint: number;
  fontWeight?: number;
}

export const TONE_STYLES: Record<CellTone, ToneStyle> = {
  groupZero: { color: "success", shade: "main", tint: TONE_ALPHA },
  groupLow: { color: "warning", shade: "main", tint: TONE_ALPHA },
  groupHigh: { color: "error", shade: "main", tint: TONE_ALPHA },
  totalRow: {
    color: "warning",
    shade: "main",
    tint: STRONG_TONE_ALPHA,
    fontWeight: TOTAL_ROW_WEIGHT,
  },
  daysCritical: {
    color: "error",
    shade: "main",
    tint: TONE_ALPHA,
    fontWeight: EMPHASIS_WEIGHT,
  },
  daysWarning: {
    color: "warning",
    shade: "main",
    tint: STRONG_TONE_ALPHA,
    fontWeight: EMPHASIS_WEIGHT,
  },
  daysNotice: { color: "warning", shade: "light", tint: TONE_ALPHA },
};

export const groupTone = (count: number): CellTone => {
  if (count === 0) return "groupZero";
  return count > GROUP_WARNING_MAX ? "groupHigh" : "groupLow";
};

/** The รวมทั้งหมด row is orange throughout, except a GROUP that sums to 0 stays green. */
export const totalsGroupTone = (count: number): CellTone =>
  count === 0 ? "groupZero" : "totalRow";

export const daysTone = (days: number | null): CellTone | null => {
  if (days === null) return null;
  if (days >= DAYS_TONE_FLOORS.critical) return "daysCritical";
  if (days >= DAYS_TONE_FLOORS.warning) return "daysWarning";
  if (days >= DAYS_TONE_FLOORS.notice) return "daysNotice";
  return null;
};

/** Branch rows colour only their GROUP cells; the รวมทั้งหมด row colours every count. */
export const pivotCellTone = (
  column: PivotColumn,
  count: number,
  isTotalsRow: boolean,
): CellTone | null => {
  if (column.kind === GROUP_KIND) return isTotalsRow ? totalsGroupTone(count) : groupTone(count);
  return isTotalsRow ? "totalRow" : null;
};

export const toneSx = (tone: CellTone | null): SxProps<Theme> => {
  if (tone === null) return {};
  const style = TONE_STYLES[tone];
  return (theme: Theme) => ({
    bgcolor: alpha(theme.palette[style.color][style.shade], style.tint),
    color: theme.palette[style.color].dark,
    fontWeight: style.fontWeight,
  });
};

// ── Map link and export ───────────────────────────────────────────────────────────────────

const GOOGLE_MAPS_URL = "https://www.google.com/maps";

export const mapsUrl = (latitude: number | null, longitude: number | null): string | null => {
  if (latitude === null || longitude === null) return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  // (0, 0) is how a shop without a pin was saved, not a place anyone delivers to.
  if (latitude === 0 && longitude === 0) return null;
  return `${GOOGLE_MAPS_URL}?q=${latitude},${longitude}`;
};

const EXPORT_FILENAME_PREFIX = "inactive-stores-over";

/** The server's own filename; used only when `Content-Disposition` cannot be read. */
export const exportFallbackFilename = (
  days: number,
  refDate: string,
  format: InactiveStoreExportFormat,
): string => `${EXPORT_FILENAME_PREFIX}${days}d-${refDate}.${format}`;
